/// <reference types="google.maps" />
import { Alert, Input, InputNumber, Modal, Slider, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { loadGoogleMaps, onGoogleAuthFailure } from '@/lib/googleMaps';

/**
 * Выбор территории офиса на карте: центр и радиус.
 *
 * По ним карта телеметрии открывается на нужном месте и не уезжает дальше
 * радиуса. Центр ставится щелчком по карте (метку можно перетащить), место
 * находится поиском по адресу — Geocoding API Google, — а радиус задаётся
 * в километрах: аэропорту хватает 3–6, городской службе нужны десятки.
 */

export interface Territory {
  lat: number;
  lon: number;
  radiusKm: number;
}

interface Props {
  open: boolean;
  /** Текущая территория офиса; null — ещё не задана. */
  value: Territory | null;
  onCancel: () => void;
  onOk: (value: Territory) => void;
}

/** Ташкент — с него начинается выбор, если у офиса координат ещё нет. */
const DEFAULT: Territory = { lat: 41.2995, lon: 69.2401, radiusKm: 6 };

export function OfficeTerritoryPicker({ open, value, onCancel, onOk }: Props) {
  const { t } = useTranslation();

  const container = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  const circle = useRef<google.maps.Circle | null>(null);
  const geocoder = useRef<google.maps.Geocoder | null>(null);

  const [point, setPoint] = useState<{ lat: number; lon: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(DEFAULT.radiusKm);
  const [mapError, setMapError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  // Карта создана — с этого момента можно ставить метку и круг.
  const [ready, setReady] = useState(false);

  // Открытие окна — начальное состояние из карточки офиса.
  useEffect(() => {
    if (!open) return;
    setPoint(value ? { lat: value.lat, lon: value.lon } : null);
    setRadiusKm(value?.radiusKm ?? DEFAULT.radiusKm);
    setSearchError(null);
    // value читается в момент открытия; родитель фиксирует его до открытия.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Карта создаётся при открытии окна и уничтожается при закрытии.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const unsubscribe = onGoogleAuthFailure((reason) => {
      if (!cancelled) setMapError(reason);
    });

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !container.current) return;
        const start = value ?? DEFAULT;
        map.current = new maps.Map(container.current, {
          center: { lat: start.lat, lng: start.lon },
          zoom: value ? 13 : 11,
          mapTypeId: 'hybrid',
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          draggableCursor: 'crosshair',
        });
        geocoder.current = new maps.Geocoder();
        setReady(true);
        map.current.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (event.latLng) setPoint({ lat: event.latLng.lat(), lon: event.latLng.lng() });
        });
        setMapError(null);
      })
      .catch((e: Error) => {
        if (!cancelled) setMapError(e.message);
      });

    return () => {
      cancelled = true;
      unsubscribe();
      marker.current?.setMap(null);
      circle.current?.setMap(null);
      marker.current = null;
      circle.current = null;
      map.current = null;
      setReady(false);
    };
    // value читается только при открытии — дальше живёт своё состояние окна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Метка центра и круг территории следуют за состоянием.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !point) return;
    const position = { lat: point.lat, lng: point.lon };

    if (!marker.current) {
      marker.current = new google.maps.Marker({ map: instance, position, draggable: true });
      marker.current.addListener('dragend', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) setPoint({ lat: event.latLng.lat(), lon: event.latLng.lng() });
      });
    } else {
      marker.current.setPosition(position);
    }

    if (!circle.current) {
      circle.current = new google.maps.Circle({
        map: instance,
        center: position,
        radius: radiusKm * 1000,
        strokeColor: '#f29a1f',
        strokeWeight: 2,
        fillColor: '#f29a1f',
        fillOpacity: 0.12,
        clickable: false,
      });
    } else {
      circle.current.setCenter(position);
      circle.current.setRadius(radiusKm * 1000);
    }
  }, [ready, point, radiusKm]);

  // Открытие и смена радиуса — показать круг целиком.
  useEffect(() => {
    const bounds = circle.current?.getBounds();
    if (bounds) map.current?.fitBounds(bounds, 24);
  }, [ready, radiusKm]);

  const search = (query: string): void => {
    const text = query.trim();
    if (!text || !geocoder.current) return;
    setSearching(true);
    setSearchError(null);
    geocoder.current
      .geocode({ address: text, region: 'uz' })
      .then(({ results }) => {
        const found = results[0];
        if (!found) {
          setSearchError(t('Ничего не найдено — уточните запрос или найдите место на карте'));
          return;
        }
        const location = found.geometry.location;
        setPoint({ lat: location.lat(), lon: location.lng() });
        if (found.geometry.viewport) map.current?.fitBounds(found.geometry.viewport);
        else map.current?.setCenter(location);
      })
      .catch((error: { code?: string }) => {
        setSearchError(
          error?.code === 'REQUEST_DENIED'
            ? t(
                'Поиск по адресу не включён: в Google Cloud включите Geocoding API и добавьте его в ограничения ключа. Место можно найти на карте вручную.',
              )
            : t('Ничего не найдено — уточните запрос или найдите место на карте'),
        );
      })
      .finally(() => setSearching(false));
  };

  return (
    <Modal
      open={open}
      title={t('Территория офиса')}
      width={860}
      okText={t('Применить')}
      cancelText={t('Отмена')}
      okButtonProps={{ disabled: !point }}
      onCancel={onCancel}
      onOk={() => point && onOk({ lat: point.lat, lon: point.lon, radiusKm })}
      destroyOnHidden
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Typography.Text type="secondary">
          {t(
            'Найдите место поиском или передвиньте карту, затем щёлкните по центру территории (базы, аэропорта, склада). Радиус — насколько далеко от центра работает техника офиса.',
          )}
        </Typography.Text>

        <Input.Search
          allowClear
          enterButton={t('Найти')}
          loading={searching}
          disabled={Boolean(mapError)}
          placeholder={t('Адрес или название места, например: Самарканд, аэропорт')}
          onSearch={search}
        />
        {searchError && <Alert type="warning" showIcon message={searchError} />}
        {mapError && <Alert type="error" showIcon message={t('Карта Google недоступна')} description={mapError} />}

        <div
          ref={container}
          style={{ height: 440, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)' }}
        />

        <Space wrap size={16} style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size={12} align="center">
            <Typography.Text>{t('Радиус, км')}</Typography.Text>
            <Slider
              min={1}
              max={100}
              value={Math.min(radiusKm, 100)}
              onChange={(next) => setRadiusKm(next)}
              style={{ width: 220 }}
            />
            <InputNumber
              min={1}
              max={200}
              precision={0}
              value={radiusKm}
              onChange={(next) => next && setRadiusKm(next)}
              style={{ width: 80 }}
            />
          </Space>
          <Typography.Text type="secondary">
            {point
              ? `${point.lat.toFixed(6)}, ${point.lon.toFixed(6)}`
              : t('Центр не выбран — щёлкните по карте')}
          </Typography.Text>
        </Space>
      </Space>
    </Modal>
  );
}
